import React from "react";
import { ITableElement } from "shared/components";
import { formatCurrency } from "shared/utils";
import { TextField, Select, MenuItem, IconButton, Menu, InputAdornment } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { PaymentSummaryCostCenter } from "shared/api/services";

interface EditableFieldWithMenuProps {
  value: string;
  onChange: (value: string) => void;
  onApplyToEmpty: () => void;
}

const EditableFieldWithMenu: React.FC<EditableFieldWithMenuProps> = ({
  value,
  onChange,
  onApplyToEmpty,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyToEmpty = () => {
    onApplyToEmpty();
    handleClose();
  };

  return (
    <>
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ marginLeft: "0px", marginRight: "-22px" }}>
              <IconButton
                onClick={handleClick}
                sx={{ 
                  padding: "0px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px !important",
                    width: "16px !important",
                    height: "16px !important",
                  },
                }}
              >
                <ContentCopy />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          width: "100%",
          "& .MuiInputBase-input": {
            fontSize: "0.8rem",
            padding: "2px 0px 2px 6px",
          },
          "& .MuiOutlinedInput-root": {
            height: "28px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "4px",
          },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "150px",
          },
          "& .MuiMenuItem-root": {
            padding: "0px 12px",
            fontSize: "12px",
          },
        }}
      >
        <MenuItem onClick={handleApplyToEmpty}>
          Replicar a todas las filas
        </MenuItem>
      </Menu>
    </>
  );
};

export interface PaymentSummaryRow {
  rowId?: string;
  codigoIdentificador?: string;
  codigoConcepto?: string;
  concepto?: string;
  nitPrestador?: string;
  prestador?: string;
  valor?: number;
  signo?: string;
  codigoCentroCostos?: string;
  nombreCentroCostos?: string;
  cdp?: string;
  rcp?: string;
  codigoMaterial?: string;
  costCenterArray?: PaymentSummaryCostCenter[];
}

export const columnsPaymentSummary = (
  onCdpChange?: (rowId: string, value: string) => void,
  cdps?: Record<string, string>,
  onCostCenterChange?: (rowId: string, value: string) => void,
  selectedCostCenters?: Record<string, string>,
  onRcpChange?: (rowId: string, value: string) => void,
  rcps?: Record<string, string>,
  onApplyCdpToEmpty?: (rowId: string) => void,
  onApplyRcpToEmpty?: (rowId: string) => void,
): ITableElement<PaymentSummaryRow>[] => {
  const columnsTable = [
    {
      field: "codigoIdentificador",
      headerName: "CODIGO IDENTIFICADOR",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.codigoIdentificador ?? ""}</>;
      },
    },
    {
      field: "codigoConcepto",
      headerName: "CODIGO CONCEPTO",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.codigoConcepto ?? ""}</>;
      },
    },
    {
      field: "concepto",
      headerName: "CONCEPTO",
      minWidth: 100,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.concepto ?? ""}</>;
      },
    },
    {
      field: "nitPrestador",
      headerName: "NIT ACREEDOR",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.nitPrestador ?? ""}</>;
      },
    },
    {
      field: "prestador",
      headerName: "ACREEDOR",
      minWidth: 100,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.prestador ?? ""}</>;
      },
    },
    {
      field: "valor",
      headerName: "VALOR",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.valor ? `${formatCurrency(params.valor)}` : ""}</>;
      },
    },
    {
      field: "signo",
      headerName: "SIGNO",
      minWidth: 50,
      renderCell(params: PaymentSummaryRow) {
        return (
          <>
            {params.signo === "+"
              ? "Concepto"
              : params.signo === "-"
                ? "Deducción"
                : (params.signo ?? "")}
          </>
        );
      },
    },
    {
      field: "codigoCentroCostos",
      headerName: "CODIGO CENTRO COSTOS",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        if (
          onCostCenterChange &&
          params.signo === "+" &&
          params.costCenterArray &&
          params.costCenterArray.length > 0 &&
          params.rowId
        ) {
          return (
            <Select
              value={selectedCostCenters?.[params.rowId] || ""}
              onChange={(e) =>
                onCostCenterChange(params.rowId!, e.target.value)
              }
              size="small"
              sx={{
                width: "100%",
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                  padding: "1px 4px",
                },
                "& .MuiOutlinedInput-root": {
                  height: "26px",
                },
                "& .MuiSelect-select": {
                  padding: "1px 4px",
                },
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Seleccionar...
              </MenuItem>
              {params.costCenterArray.map((center) => (
                <MenuItem key={center.id} value={center.code}>
                  {center.code}
                </MenuItem>
              ))}
            </Select>
          );
        }
        return <>{""}</>;
      },
    },
    {
      field: "nombreCentroCostos",
      headerName: "NOMBRE CENTRO COSTOS",
      minWidth: 100,
      renderCell(params: PaymentSummaryRow) {
        if (selectedCostCenters && params.rowId && params.costCenterArray) {
          const selectedCode = selectedCostCenters[params.rowId];
          if (selectedCode) {
            const selectedCenter = params.costCenterArray.find(
              (center) => center.code === selectedCode,
            );
            if (selectedCenter) {
              return <>{selectedCenter.name}</>;
            }
          }
        }
        return <>{""}</>;
      },
    },
    {
      field: "cdp",
      headerName: "CDP",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        if (onCdpChange && params.rowId) {
          return (
            <EditableFieldWithMenu
              value={cdps?.[params.rowId] || ""}
              onChange={(value) => onCdpChange(params.rowId!, value)}
              onApplyToEmpty={() => {
                if (onApplyCdpToEmpty) {
                  onApplyCdpToEmpty(params.rowId!);
                }
              }}
            />
          );
        }
        return <>{params.cdp ?? ""}</>;
      },
    },
    {
      field: "rcp",
      headerName: "RPC",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        if (onRcpChange && params.rowId) {
          return (
            <EditableFieldWithMenu
              value={rcps?.[params.rowId] || ""}
              onChange={(value) => onRcpChange(params.rowId!, value)}
              onApplyToEmpty={() => {
                if (onApplyRcpToEmpty) {
                  onApplyRcpToEmpty(params.rowId!);
                }
              }}
            />
          );
        }
        return <>{params.rcp ?? ""}</>;
      },
    },
    {
      field: "codigoMaterial",
      headerName: "CODIGO MATERIAL",
      minWidth: 80,
      renderCell(params: PaymentSummaryRow) {
        return <>{params.codigoMaterial ?? ""}</>;
      },
    },
  ];

  return columnsTable;
};
